// @flow

import countBy from 'lodash/countBy';
import first from 'lodash/first';
import {
  compact,
  get,
  mapValues,
  partition,
  isArray,
  isEmpty,
  reduce,
  map,
  toString,
  trim,
  join,
  times,
  orderBy
} from 'lodash/fp';
import moment from 'moment';
import sample from 'lodash/sample';
import sampleSize from 'lodash/sampleSize';
import slice from 'lodash/slice';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';

import config from '../config';
import { GOLF } from '../constants/challengeOneSportIds';
import * as columnTypes from '../constants/columnTypes';
import t from './translate';
import { largestMatchingString } from './stringUtils';
import {
  COMPLETED,
  IN_MANUAL_REVIEW,
  IN_PROGRESS,
  IN_REVIEW
} from '../constants/challengeStatusTypes';

import { getFactoredEntityPositionSalary } from '../selectors/salaryExpenditureSelector';

const mapValuesWithKey = mapValues.convert({ cap: false });

type PositionRules = {
  [positionId: string]: {
    id: string,
    key: string,
    max: number,
    min: number
  }
};

// Take them out so they don't change instances
const upcomingColumns = [
  columnTypes.FEE,
  columnTypes.ENTRY_COUNT,
  columnTypes.PAYOUT,
  columnTypes.PRIZE
];
const enteredColumns = [columnTypes.POSITION, columnTypes.POINTS, columnTypes.WINNINGS];
const defaultColumns = [columnTypes.ENTRY_COUNT, columnTypes.PAYOUT, columnTypes.PRIZE];
const compatibleChallengesColumns = [
  columnTypes.FEE,
  columnTypes.ENTRY_COUNT,
  columnTypes.PRIZE,
  columnTypes.COUNTER
];
const editLineUpColumns = [
  columnTypes.FEE,
  columnTypes.ENTRY_COUNT,
  columnTypes.PAYOUT,
  columnTypes.PRIZE,
  columnTypes.CHECK_BOX
];

export function getColumns({
  live,
  entered,
  compatibleChallenges,
  editLineUp
}: {
  live: boolean,
  entered: boolean,
  compatibleChallenges: boolean,
  editLineUp: boolean
}) {
  if (compatibleChallenges) {
    return compatibleChallengesColumns;
  }
  if (editLineUp) {
    return editLineUpColumns;
  }
  if (!live) {
    return upcomingColumns;
  }
  if (entered) {
    return enteredColumns;
  }
  return defaultColumns;
}

export const convertFantasyInsiderPosition = (pos: string) => (pos === 'WFB' ? 'W/FB' : pos);

export const convertPositionForFantasyInsider = (pos: string) => (pos === 'W/FB' ? 'WFB' : pos);

export function getChallengeGroupName(challenges: Array<Object>) {
  const names: Array<string> = map('name', challenges);
  const matchingStr = largestMatchingString(names);
  const trimmedMatchingStr = trim(matchingStr || '');
  return trimmedMatchingStr.length >= 8 ? trimmedMatchingStr : join(', ', names);
}

export function getRecentlyCompleted(challenges: Array<Object>): Array<Object> {
  return challenges.filter(ch => {
    const days = moment().diff(moment(ch.startTime), 'days');
    return days < 5;
  });
}

export function checkUserLevel(levels, userLevel) {
  return isEmpty(levels) || userLevel === undefined || get(userLevel, levels) !== false;
}

export function isCasualChallenge(levels) {
  // a challenge is casual if it cannot be entered by users rated sharks (4) or super sharks (5)
  if (!levels) return false;

  return !levels[4] && !levels[5];
}

function objectToArray(obj = {}) {
  return Object.entries(obj).reduce(
    (memo, [key, value]) => [...memo, ...times(() => key, value)],
    []
  );
}

function filterPlayersForAutoFill(players, lineUp) {
  const ignoreLabels = ['O', 'D', 'SUS', 'SCR', '\u2716'];
  return players.filter(
    player =>
      lineUp.every(p => p.id !== player.id) && // remove players you've already chosen
      // remove players that are "OUT" or "DOUBTFUL" or "SUSPENDED"
      player.labels.every(label => !ignoreLabels.includes(label.name))
  );
}

// @private
export function getRandomRemainingPositions(
  positionRules: PositionRules,
  lineUp: Array<Object>,
  playerQuota: number
) {
  const lineUpPositionsFilled = countBy(lineUp, player => player.positionId);

  const merged = mapValues(pos => Math.max(pos.min, lineUpPositionsFilled[pos.id] || 0))(
    positionRules
  );

  const totalMerged = sumBy(Object.values(merged));
  const remainingQuota = playerQuota - totalMerged;

  const remainingPositions = mapValuesWithKey((value, key) => {
    const position = positionRules[key];
    return position ? position.max - value : 0;
  })(merged);

  // remaining pool of positions to randomly choose from.
  const remainingPositionsArray = objectToArray(remainingPositions);

  const shuffledPositionsArray = sampleSize(remainingPositionsArray, remainingQuota);

  const shuffledRemainingObject = countBy(shuffledPositionsArray, positionId => positionId);

  // final count of all positions to be chosen for the lineup
  const finalTotalPositions = mapValuesWithKey(
    (value, key) => value + (shuffledRemainingObject[key] || 0)
  )(merged);

  // final total minus the lineup you have already chosen
  const finalRemainingPositions = mapValuesWithKey(
    (value, key) => value - (lineUpPositionsFilled[key] || 0)
  )(finalTotalPositions);
  return objectToArray(finalRemainingPositions);
}

// @private
function optionalCondition(dependsOn, f) {
  if (dependsOn) {
    return f();
  }
  return true;
}

export function canAddPlayerToLineUp(
  player: Object,
  {
    positionId,
    playersPerSideCap,
    playersPerGame,
    salaryCap,
    selectedPlayers,
    selectedTeams
  }: {
    positionId: string,
    playersPerSideCap: number,
    playersPerGame: Object,
    salaryCap: number,
    selectedPlayers: Array<Object>,
    selectedTeams: Array<Object>
  }
) {
  const lineUp = [...selectedPlayers, ...selectedTeams];

  return (
    lineUp.every(p => p.id !== player.id) &&
    optionalCondition(positionId, () => player.positions.find(p => p.key === positionId)) &&
    optionalCondition(playersPerSideCap, () => {
      const playersInSameTeam = selectedPlayers.filter(p => p.teamId === player.teamId);
      return playersInSameTeam.length < playersPerSideCap;
    }) &&
    optionalCondition(playersPerGame, () => {
      const playersInSameGame = selectedPlayers.filter(p => p.gameId === player.gameId);
      return !playersPerGame || playersInSameGame.length < get('max', playersPerGame);
    }) &&
    optionalCondition(salaryCap, () => {
      const currentSalary = lineUp.reduce((memo, p) => memo + p.salary, 0);
      return currentSalary + player.salary <= salaryCap;
    })
  );
}

export function keyEntitiesByPosition(players: Array<Object>, teams: Array<Object>) {
  const selections = reduce(
    (memo, entity) => {
      const pos = get('position.key', entity) || get('[0].position.key', players);
      const isTeam = !!entity.isTeam;
      const idStr = isTeam ? `TEAM_${entity.id}` : `PLAYER_${entity.id}`;
      return {
        ...memo,
        [pos]: memo[pos] ? [...memo[pos], { idStr, isTeam }] : [{ idStr, isTeam }]
      };
    },
    {},
    [...players, ...teams]
  );

  return selections;
}

// this function is for players only, NOT teams.
export function getCheapestPlayerInPosition(
  playerPool: Array<Object>,
  positionId: string,
  selectedPlayers: Array<Object>,
  selectedTeams: Array<Object>,
  playersPerSideCap: number,
  playersPerGame: Object,
  salaryCap: number
) {
  const filteredPlayerPool = playerPool.filter(player =>
    canAddPlayerToLineUp(player, {
      positionId,
      playersPerSideCap,
      playersPerGame,
      salaryCap,
      selectedPlayers,
      selectedTeams
    })
  );

  filteredPlayerPool.sort((player1, player2) => {
    const factoredSalary1 = getFactoredEntityPositionSalary(
      player1.salary,
      player1.positions,
      positionId
    );
    const factoredSalary2 = getFactoredEntityPositionSalary(
      player2.salary,
      player2.positions,
      positionId
    );

    return factoredSalary1 - factoredSalary2;
  });

  return filteredPlayerPool[0];
}

export function getCheapestPossibleLineUp(
  players: Array<Object>,
  teams: Array<Object>,
  remainingPositions: Array<string>,
  selectedPlayers: Array<Object>,
  selectedTeams: Array<Object>,
  playerQuota: number,
  teamQuota: number,
  playersPerSideCap: number,
  playersPerGame: Object,
  salaryCap: number
): ?Object {
  const playerPicks = [...selectedPlayers];
  const filteredPlayers = filterPlayersForAutoFill(players, playerPicks);

  // cheapest team already picked here, no need to consider teams after this.
  const teamPicks =
    teamQuota > selectedTeams.length
      ? sortBy(teams, 'salary').slice(0, teamQuota - selectedTeams.length)
      : selectedTeams;

  for (let i = 0; i < remainingPositions.length; i += 1) {
    const position = remainingPositions[i];
    const player = getCheapestPlayerInPosition(
      filteredPlayers,
      position,
      playerPicks,
      teamPicks,
      playersPerSideCap,
      playersPerGame,
      salaryCap
    );
    if (!player) {
      return null;
    }
    playerPicks.push(player);
  }

  const totalTeamsSalary = sumBy(teamPicks, 'salary');

  const totalPlayersSalary = playerPicks.reduce(
    (sum, player) =>
      sum + getFactoredEntityPositionSalary(player.salary, player.positions, player.positionId),
    0
  );

  if (totalPlayersSalary + totalTeamsSalary > salaryCap) {
    // new line up exceeds the salary cap.
    // this will occur when adding a team tips the salary cap over the limit
    return null;
  }

  return {
    players: playerPicks.map(player => player.id),
    teams: teamPicks.map(team => team.id)
  };
}

export function isInProgress(statusId) {
  return [IN_PROGRESS, IN_MANUAL_REVIEW].includes(statusId);
}

export function showCompleteChallengeStartTime(statusId) {
  return statusId > IN_REVIEW && statusId !== IN_MANUAL_REVIEW;
}

export function isMatchComplete(statusId) {
  return statusId > IN_PROGRESS && statusId !== IN_MANUAL_REVIEW;
}

export function enableChallengePolling(statusId) {
  return statusId < COMPLETED || statusId === IN_MANUAL_REVIEW;
}

export function isChallengeComplete(statusId) {
  return statusId >= COMPLETED && statusId !== IN_MANUAL_REVIEW;
}

export function getRandomPlayerInPosition(
  playerPool: Array<Object>,
  positionId: string,
  selectedPlayers: Array<Object>,
  selectedTeams: Array<Object>,
  playersPerSideCap: number,
  playersPerGame: Object,
  salaryCap: number,
  enableSmartLineUp: boolean
): ?Object {
  const currentSalary = sumBy([...selectedTeams, ...selectedPlayers], 'salary');
  const eligiblePlayers = playerPool.filter(player => {
    const playersInSameTeam = selectedPlayers.filter(p => p.teamId === player.teamId);
    const playersInSameGame = selectedPlayers.filter(p => p.gameId === player.gameId);

    return (
      !player.dualAccepted && // Exclude DA players
      player.positions.find(pos => pos.key === positionId) &&
      selectedPlayers.every(p => p.id !== player.id) &&
      (playersInSameTeam.length < playersPerSideCap || playersPerSideCap === 0) &&
      (!playersPerGame || playersInSameGame.length < get('max', playersPerGame)) &&
      currentSalary + player.salary <= salaryCap
    );
  });

  const randomPlayer = enableSmartLineUp
    ? sample(slice(orderBy(['salary'], ['desc'], eligiblePlayers), 0, 10))
    : sample(eligiblePlayers);

  if (!randomPlayer) {
    return null;
  }

  return {
    ...randomPlayer,
    positionId
  };
}

export function getRandomPlayerInPositionAndCheckLineUp(
  playerPool: Array<Object>,
  teamPool: Array<Object>,
  remainingPositions: Array<string>,
  positionId: string,
  selectedPlayers: Array<Object>,
  selectedTeams: Array<Object>,
  playersPerSideCap: number,
  playersPerGame: Object,
  salaryCap: number,
  playerQuota: number,
  teamQuota: number,
  enableSmartLineUp: boolean
): ?Object {
  if (playerPool.length === 0) {
    return null;
  }
  const player = getRandomPlayerInPosition(
    playerPool,
    positionId,
    selectedPlayers,
    selectedTeams,
    playersPerSideCap,
    playersPerGame,
    salaryCap,
    enableSmartLineUp
  );

  if (!player) {
    return null;
  }

  if (
    getCheapestPossibleLineUp(
      playerPool,
      teamPool,
      remainingPositions,
      [player, ...selectedPlayers],
      selectedTeams,
      playerQuota,
      teamQuota,
      playersPerSideCap,
      playersPerGame,
      salaryCap
    )
  ) {
    return player;
  }

  // we chose a player that fits with the criteria,
  // but it's now impossible to complete the lineup
  // and stay under the salary cap.
  // so pick again but filter out all players >= this
  // player's salary.
  return getRandomPlayerInPositionAndCheckLineUp(
    playerPool.filter(p => p.salary < player.salary),
    teamPool,
    remainingPositions,
    positionId,
    selectedPlayers,
    selectedTeams,
    playersPerSideCap,
    playersPerGame,
    salaryCap,
    playerQuota,
    teamQuota,
    enableSmartLineUp
  );
}

export function getRandomPlayersAndTeams(
  players: Array<Object>,
  teams: Array<Object>,
  positionRules: PositionRules,
  lineUp: Array<Object>,
  playerQuota: number,
  teamQuota: number,
  playersPerSideCap: number,
  playersPerGame: Object,
  salaryCap: number,
  shareQuota: boolean,
  enableSmartLineUp: boolean,
  retryCount: number = 0
) {
  const remainingPositions = getRandomRemainingPositions(positionRules, lineUp, playerQuota);

  // the challenge rules include a team
  const teamRequired = !shareQuota && teamQuota > 0;

  const [selectedTeams, newPlayers] = partition('isTeam')(lineUp);

  const newTeams = teamRequired
    ? [
        ...selectedTeams,
        ...sampleSize(
          teams.filter(team => selectedTeams.every(tm => tm.id !== team.id)),
          teamQuota - selectedTeams.length
        )
      ].map(team => ({
        ...team,
        positionId: team.positions[0].id,
        isTeam: true
      }))
    : [];

  const filteredPlayers = filterPlayersForAutoFill(players, lineUp);

  for (let i = 0; i < remainingPositions.length; i += 1) {
    const position = remainingPositions[i];
    const player = getRandomPlayerInPositionAndCheckLineUp(
      filteredPlayers,
      teams,
      remainingPositions.slice(i + 1),
      position,
      newPlayers,
      newTeams,
      playersPerSideCap,
      playersPerGame,
      salaryCap,
      playerQuota,
      teamQuota,
      enableSmartLineUp
    );

    if (!player) {
      if (retryCount >= 7) {
        throw new Error(`
          oh dear, cant find a player.
          This should only occur if its IMPOSSIBLE to create a team with the given criteria
        `);
      }

      return getRandomPlayersAndTeams(
        players,
        teams,
        positionRules,
        lineUp,
        playerQuota,
        teamQuota,
        playersPerSideCap,
        playersPerGame,
        salaryCap,
        shareQuota,
        enableSmartLineUp,
        retryCount + 1
      );
    }

    newPlayers.push(player);
  }

  const res = reduce(
    (memo, { positionId, idStr, isTeam }) => ({
      ...memo,
      [positionId]: memo[positionId]
        ? [...memo[positionId], { idStr, isTeam }]
        : [{ idStr, isTeam }]
    }),
    {},
    [...newPlayers, ...newTeams]
  );

  return res;
}

export function getDateString(date: string) {
  return moment(date)
    .locale(config.settings.language)
    .calendar(null, {
      sameDay: `[${t('Today')}] ll`,
      nextDay: `[${t('Tomorrow')}] ll`,
      nextWeek: 'dddd ll',
      lastDay: `[${t('Yesterday')}] dddd`,
      lastWeek: `[${t('Last')}] dddd`,
      sameElse: 'll'
    });
}

export function getSelectionAndBonus(lineupSelection) {
  const { players, teams } = lineupSelection;
  const selections = keyEntitiesByPosition(players, teams);
  const bonuses: {
    [type: string]: string
  } = reduce((memo, player) => {
    if (player.bonus) {
      return {
        ...memo,
        [player.bonus.key]: toString(player.id)
      };
    }
    return memo;
  }, {})(players);

  return { selections, bonuses };
}

type Errors = {
  message?: 'string',
  messages?: Array<{ key: string, message: string }>
};

// maps an error object into an array of strings.
// NOTE: fp-api normalizes hard errors into an object that always contains messages
export function mapErrorMessages(errors: Errors) {
  if (isArray(errors)) {
    return [];
  }
  if (!errors) return [];
  if (errors.message) {
    return [errors.message];
  }
  return map('message')(errors.messages);
}

export const COMPETITION_AFL = 8;
export const COMPETITION_NBA = 11;
export const COMPETITION_MLB = 95;
export const COMPETITION_NFL = 6;
export const COMPETITION_NRL = 26;
export const COMPETITION_ORIGIN = 31;

export function isChallengeNBA(competitionId) {
  return competitionId === COMPETITION_NBA;
}

export function isChallengeMLB(competitionId) {
  return competitionId === COMPETITION_MLB;
}

export function isChallengeAFL(competitionId) {
  return competitionId === COMPETITION_AFL;
}

export function isChallengeGolf(oneSportId) {
  return toString(oneSportId) === GOLF;
}

export const isFantasyInsiderSport = competitionId =>
  [COMPETITION_AFL, COMPETITION_NRL, COMPETITION_NBA, COMPETITION_NFL].includes(competitionId);

export const getPlayerBoardTableInfo = ({ isRacing, showPlayerSelections }) =>
  isRacing
    ? {
        columns: compact([
          {
            field: 'number',
            label: 'No',
            showInPlayerBar: true
          },
          {
            field: 'barrier',
            label: 'Barrier',
            initial: true,
            showInPlayerBar: true
          },
          showPlayerSelections
            ? { field: 'popularity', label: 'Sel', format: 'lineGraph' }
            : undefined,
          {
            field: 'win',
            label: 'Win',
            format: '0.00',
            hasProjectedScore: true,
            showStarSymbolForScheduled: true
          },
          {
            field: 'place',
            label: 'Place',
            format: '0.00',
            hasProjectedScore: true,
            size: 'large',
            mobileSize: 'small',
            showStarSymbolForScheduled: true
          },
          {
            field: 'score',
            label: 'Pts'
          }
        ]),
        defaultColumn: {
          field: 'score',
          sort: 'desc'
        }
      }
    : {
        columns: compact([
          {
            field: 'nameLookup',
            label: 'Name',
            initial: true,
            showInPlayerBar: true
          },
          showPlayerSelections
            ? { field: 'popularity', label: 'Sel', format: 'lineGraph' }
            : undefined,
          {
            field: 'valueForMoney',
            label: 'Value',
            format: 'valueGraph'
          },
          {
            field: 'salary',
            label: 'Salary',
            size: 'large',
            format: '$0,0'
          },
          {
            field: 'score',
            label: 'Pts'
          }
        ]),
        defaultColumn: {
          field: 'score',
          sort: 'desc'
        }
      };

export const getPlayerSelectionTableInfo = ({ isRacing, showPlayerSelections }) =>
  isRacing
    ? {
        columns: compact([
          {
            field: 'number',
            label: 'No',
            showInPlayerBar: true
          },
          {
            field: 'barrier',
            label: 'Barrier',
            initial: true,
            showInPlayerBar: true
          },
          showPlayerSelections
            ? { field: 'popularity', label: 'Sel', format: 'lineGraph' }
            : undefined,
          {
            field: 'weight',
            label: 'Weight'
          },
          {
            field: 'form',
            label: 'Form',
            format: 'string',
            size: 'large',
            sortable: false
          },
          {
            field: 'winProjected',
            label: 'Win',
            format: '0.00',
            showStarSymbolForScheduled: true,
            bold: true,
            disableWhenUnselected: true
          },
          {
            field: 'placeProjected',
            label: 'Place',
            format: '0.00',
            showStarSymbolForScheduled: true
          }
        ]),
        defaultColumn: {
          field: 'placeProjected',
          sort: 'asc',
          format: '0.00',
          size: 'small'
        }
      }
    : {
        columns: compact([
          {
            field: 'nameLookup',
            label: 'Name',
            initial: true,
            showInPlayerBar: true
          },
          showPlayerSelections
            ? { field: 'popularity', label: 'Sel', format: 'lineGraph' }
            : undefined,
          {
            field: 'average',
            label: 'Avg'
          },
          {
            field: 'form',
            label: 'Form'
          },
          {
            field: 'salary',
            label: 'Salary',
            size: 'large',
            format: '$0,0'
          }
        ]),
        defaultColumn: {
          field: 'salary',
          sort: 'desc',
          format: '$0,0'
        }
      };
